// src/encounter/use-note-scroll.ts
//
// TS-3 · frame v2 only. The one-continuous-scroll note (Samer, Loom 5 Oct 2026; R6c Patient Workspace
// `onScroll` / `jump`, `:799-810`): every S·O·A·P section is on screen in one scroll, the track's tabs
// are jump links, and the tab of the section in view is highlighted.
//
// THE PAGE STILL HEARS WHICH SECTION YOU ARE IN. Leaving a section (by tab or by scrolling past it)
// calls the page's ONE step handler with that section's first step, so everything a section change has
// always done still runs: the autosave flush and the Risk bridge's encounter-level save (EncounterMetaData
// Fall/Pain are saved on a step change, never per keystroke — `use-risk-assessments.ts`). Without this,
// Risk answers typed in one scroll would wait for unmount or a hidden tab. It is called only when the
// SECTION changes, never on every scroll event.
//
// A PAGE-DRIVEN STEP still lands where it should. The page moves the step itself in a few places (Add
// Order, an ambient order, an order set → `setCurrentStep('ap')`; the Sign list's links); when the step
// changes to one this hook did not ask for, the note scrolls to that step's section.
//
// A JUMP MOVES KEYBOARD FOCUS (design desk, 5 Oct): a track tab lands focus on the target section's
// heading (made focusable with tabIndex -1, focused without a second scroll), so a keyboard or screen-
// reader user continues from the section they asked for — not from the tab they left. Only a jump the
// clinician made moves focus; the page's own moves (an order opening its form) and the spy never do.
//
// The scroll area is the frame's step canvas (`data-slot="pa-step-canvas"`, the DS EncounterShell's
// ONLY scroller); with none found (a host without the frame, jsdom without layout), nothing is spied and
// a jump only moves the highlight — never an error.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaStepKey } from './lib/encounter-steps';
import { firstStepOfSection, sectionInView, sectionOf, sectionsFor, type SoapSectionId } from './lib/soap-ribbon';

/** A section is "in view" once its top is within this many px of the top of the scroll area. */
export const SPY_LINE_PX = 120;
/** A jump leaves this gap above the section's top edge. */
export const JUMP_GAP_PX = 8;

/** The attribute each rendered section carries (one per section, in document order). */
export const SECTION_ATTR = 'data-soap-section';

/** The scroll area that holds `el`: the frame's step canvas, else the nearest scrolling ancestor. */
export function scrollAreaOf(el: HTMLElement): HTMLElement | null {
  const canvas = el.closest<HTMLElement>('[data-slot="pa-step-canvas"]');
  if (canvas) return canvas;
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = window.getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll') return p;
  }
  return null;
}

export interface UseNoteScrollArgs {
  /** One-scroll mode AND the note is on screen (Encounter tab, no centre panel). */
  enabled: boolean;
  nav: readonly PaStepKey[];
  currentStep: PaStepKey;
  onStepChange: (step: PaStepKey) => void;
}

export interface NoteScroll {
  /** The callback ref for the element that holds every section. */
  bindRoot: (el: HTMLDivElement | null) => void;
  /** The section in view (what the track highlights). */
  activeSection: SoapSectionId | undefined;
  /** Scroll the note to section `id`, highlight it and move keyboard focus to its heading (a track tab).
   *  `{ focus: false }` for a move the clinician did not make (an order handed to P): focus stays put. */
  jump: (id: SoapSectionId, opts?: { focus?: boolean }) => void;
}

/** The heading a jump focuses: the section's own heading (the DS NoteSection's h2), else the section. */
export function sectionHeadingOf(section: HTMLElement): HTMLElement {
  return section.querySelector<HTMLElement>('h2, h3') ?? section;
}

export function useNoteScroll({ enabled, nav, currentStep, onStepChange }: UseNoteScrollArgs): NoteScroll {
  const firstSection = sectionsFor(nav)[0]?.id;
  // What is actually visible at the top of a fresh scroll: the first section. A deep link to a later
  // step jumps there once the sections have rendered (the page-step effect below).
  const [inView, setInView] = useState<SoapSectionId | undefined>(firstSection);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);

  // Latest values for the scroll handler, which must not be re-bound on every render.
  const latest = useRef({ nav, currentStep, onStepChange, inView });
  useEffect(() => {
    latest.current = { nav, currentStep, onStepChange, inView };
  });
  /** The step this hook last asked the page for — so the page's echo is not read as a page jump. */
  const asked = useRef<PaStepKey | null>(null);
  /** The scrollTop a jump landed on: the scroll event it causes keeps the section that was clicked
   *  (a short last section can never reach the line, and a click must still highlight it). */
  const pinnedTop = useRef<number | null>(null);

  /** Tell the page we are in section `id` (its first step), unless its step is already one that section
   *  holds (TS-3: the A/P step is held by A and by P, so moving between them asks nothing). */
  const follow = useCallback((id: SoapSectionId) => {
    const { nav: n, currentStep: step, onStepChange: change } = latest.current;
    if (sectionsFor(n).find((g) => g.id === id)?.items.includes(step)) return;
    const first = firstStepOfSection(n, id);
    if (!first) return;
    asked.current = first;
    change(first);
  }, []);

  /** Scroll section `id` to the top of the scroll area (instantly, as the design does). Returns the
   *  section element it found (null with no root or no such section). */
  const scrollTo = useCallback(
    (id: SoapSectionId): HTMLElement | null => {
      setInView(id);
      if (!root) return null;
      const section = root.querySelector<HTMLElement>(`[${SECTION_ATTR}="${id}"]`);
      const area = scrollAreaOf(root);
      if (!area || !section) return section;
      const offset = section.getBoundingClientRect().top - area.getBoundingClientRect().top;
      const max = Math.max(0, area.scrollHeight - area.clientHeight);
      const top = Math.min(max, Math.max(0, area.scrollTop + offset - JUMP_GAP_PX));
      pinnedTop.current = top;
      area.scrollTop = top;
      return section;
    },
    [root],
  );

  const jump = useCallback(
    (id: SoapSectionId, opts?: { focus?: boolean }) => {
      const section = scrollTo(id);
      if (section && opts?.focus !== false) {
        const heading = sectionHeadingOf(section);
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      follow(id);
    },
    [scrollTo, follow],
  );

  // THE SPY: on scroll (one measurement per frame), the section whose top has reached the line.
  useEffect(() => {
    if (!enabled || !root) return;
    const area = scrollAreaOf(root);
    if (!area) return;
    let frame = 0;
    let scheduled = false;
    const measure = () => {
      scheduled = false;
      if (pinnedTop.current !== null) {
        if (Math.abs(area.scrollTop - pinnedTop.current) <= 2) return;
        pinnedTop.current = null;
      }
      const base = area.getBoundingClientRect().top;
      const tops = Array.from(root.querySelectorAll<HTMLElement>(`[${SECTION_ATTR}]`)).map((el) => ({
        id: el.getAttribute(SECTION_ATTR) as SoapSectionId,
        top: el.getBoundingClientRect().top - base,
      }));
      // "At the end" only when there is somewhere to scroll and we are there: a note shorter than the
      // area must not highlight its last section on load.
      const scrollable = area.scrollHeight > area.clientHeight + 4;
      const atEnd = scrollable && area.scrollTop > 0 && area.scrollTop + area.clientHeight >= area.scrollHeight - 4;
      const id = sectionInView(tops, SPY_LINE_PX, atEnd);
      if (id && id !== latest.current.inView) {
        setInView(id);
        follow(id);
      }
    };
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      frame = window.requestAnimationFrame(measure);
    };
    area.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      area.removeEventListener('scroll', onScroll);
      if (scheduled) window.cancelAnimationFrame(frame);
    };
  }, [enabled, root, follow]);

  // THE PAGE'S STEP: a step this hook did not ask for (a deep link, Add Order, a Sign-list link) moves
  // the note to that step's section. When one scroll turns on, or the note mounts again (back from the
  // Patient chart tab), the note opens at the section the clinician is standing in.
  const live = useRef(false);
  useEffect(() => {
    if (!enabled || !root) {
      live.current = false;
      return;
    }
    const turnedOn = !live.current;
    live.current = true;
    if (!turnedOn && asked.current === currentStep) return;
    asked.current = null;
    const n = latest.current.nav;
    const id = n.includes(currentStep) ? sectionOf(n, currentStep) : undefined;
    if (id && (turnedOn || id !== latest.current.inView)) scrollTo(id);
  }, [enabled, root, currentStep, scrollTo]);

  return { bindRoot: setRoot, activeSection: inView, jump };
}
